import classnames from 'classnames';
import { useMemo, useState } from 'react';
import { $a } from 'shared-base';
import { sendAnalytics, sendAnalyticsD } from '../../globals';
import { useFuzzySearchQ } from '../../hooks/useFuzzySearch';
import CardPlay2 from '../CardPlay2/CardPlay2';
import OrderGenerator from '../OrderGenerator/OrderGenerator';
import Search from '../Search/Search';
import Segment from '../Segment/Segment';
import VirtualGallery from '../VirtualGallery/VirtualGallery';
import { itemActions } from './GeneratorBrowser.data';
import { Content, Row, Wrapper } from './GeneratorBrowser.style';

export type GeneratorBrowserProps = {
  generators: IGenerator[];
  isLoading: boolean;
  onAction: OnAction;
  whiteThumbnails: boolean;
  onThumbChange: (value: string) => void;
  children?: React.ReactNode;
  spawn: boolean;
};

export function GeneratorBrowser(props: GeneratorBrowserProps) {
  const { generators, isLoading, spawn, whiteThumbnails } = props;
  const [tag, setTag] = useState('all');
  const [q, setQ] = useState('');

  const generatorsQ = useFuzzySearchQ(generators, ['id', 'name', 'tags'], q);

  const generatorsT = useMemo(() => {
    if (tag === 'all') return generatorsQ;
    return generatorsQ.filter((t: IGenerator) => t.tags.includes(tag));
  }, [generatorsQ, tag]);

  function onClick(item: any) {
    const verb = spawn ? 'pick' : 'playground';

    sendAnalytics('Generator Picked', {
      generatorId: item.id,
      name: item.name,
      spawn,
    });

    props.onAction(
      $a('GENERATORS', verb, item.id, {
        q,
        tag,
      })
    );
  }

  function onSearch(q: string) {
    setQ(q);
    if (!q) return;
    sendAnalyticsD('Generator Search', { q });
  }

  function onClear() {
    setQ('');
    sendAnalytics('Generator Clear Search');
  }

  function onTagChange(value: string) {
    setTag(value);
  }

  const className = classnames('GeneratorBrowser-wrapper', {
    small: spawn,
  });

  function renderCard(props: any) {
    return <CardPlay2 {...props} />;
  }

  function onThumbChange(value: string) {
    props.onThumbChange(value);
  }

  function renderOrderGenerator() {
    return (
      <OrderGenerator spawn={spawn} onCta={() => props.onAction($a('GENERATORS', 'orderCustom'))} />
    );
  }

  const thumbsColor = whiteThumbnails ? 'white' : 'colorful';

  return (
    <Wrapper className={className} data-testid='GeneratorBrowser-wrapper'>
      <Row>
        {props.children}
        <Search q={q} onSearch={onSearch} onClear={onClear} />
        <Segment
          value={tag}
          maxWidth={630}
          data={[
            'all',
            'building',
            'furniture',
            'urban',
            'household',
            'medieval',
            'weapon',
            'outdoor',
            'kitchen',
          ]}
          onChange={(value: string) => onTagChange(value)}
        />
        <Segment
          value={thumbsColor}
          maxWidth={150}
          data={['white', 'colorful']}
          onChange={(value: string) => onThumbChange(value)}
        />
      </Row>
      <Content>
        <VirtualGallery
          isLoading={isLoading}
          baseImageUrl=''
          items={generatorsT}
          onClick={onClick}
          itemActions={spawn ? [] : itemActions}
          onAction={props.onAction}
          small={spawn}
          itemSize={spawn ? { width: 180, height: 230 } : { width: 230, height: 300 }}
          fixedComponent={renderOrderGenerator}
          hideId
        >
          {renderCard}
        </VirtualGallery>
      </Content>
    </Wrapper>
  );
}

export default GeneratorBrowser;
